import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, RadioTower, Landmark, Bell, Vote, Siren, CalendarDays, Receipt, MapPinned, Newspaper } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PageHeader, SectionHeader, RichSection } from '../components/Shared';
import { PositionBox, FactBadge } from '../components/FactCheck';
import { NextStepBand } from '../components/NextStepBand';

const WHAT_RESIDENTS_SEE = [
  { icon: Landmark, title: 'Council decisions', body: 'What was decided, what it costs, and what it means, in plain language, delivered to residents.' },
  { icon: Bell, title: 'Public notices', body: 'Road closures, service changes, and legal notices that people actually see in time.' },
  { icon: Vote, title: 'Election information', body: 'Dates, methods, candidates, and how to vote, without hunting through buried pages.' },
  { icon: Siren, title: 'Emergency information', body: 'Urgent updates that reach people where they already are.' },
  { icon: CalendarDays, title: 'Community events', body: 'What is happening across Ward 8 and Kawartha Lakes.' },
  { icon: Receipt, title: 'Taxpayer information', body: 'Budgets, spending, and value questions, explained simply with sources.' },
  { icon: MapPinned, title: 'Municipal issues', body: 'Live issue patterns, like PITA\'s public map, so problems are visible instead of buried.' },
];

const ALREADY_BUILT = [
  { title: 'PITA', body: 'Report It. Map It. Track It. Get Answers.', url: '/issue-tracker', label: 'Open PITA' },
  { title: 'The Newsroom', body: 'Community stories from the Kawartha People\'s Desk.', url: '/newsroom', label: 'Visit Newsroom' },
  { title: 'Public Alerts', body: 'Subscribe once, stay informed.', url: '/alerts', label: 'See Alerts' },
  { title: 'Council Meetings', body: 'Agendas and plain-language notes.', url: '/meetings', label: 'See Meetings' },
];

export default function MediaForThePeople() {
  return (
    <div>
      <PageHeader
        pageKey="media-for-the-people"
        kicker="MY PLATFORM"
        title="BRING THE INFORMATION TO THE PEOPLE."
        subtitle="Do not make residents hunt for important public information. Bring the information to them."
      />
      <RichSection id="media-sections.intro" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" />

      {/* The principle */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="media-principle-section">
        <div className="bg-white rounded-2xl border-l-4 border-[#C9A84C] border border-slate-200 p-6 sm:p-8" data-testid="media-principle-note">
          <div className="flex items-start gap-3">
            <RadioTower className="h-6 w-6 text-[#C9A84C] shrink-0 mt-0.5" />
            <div>
              <p className="font-serif font-black text-[#1a2744] text-xl leading-snug">The principle is simple.</p>
              <p className="text-slate-700 mt-2 leading-relaxed">Important public information should find residents, not the other way around. Too much of what matters, decisions, notices, costs, and deadlines, sits buried where almost nobody looks. Modern public communication can fix that.</p>
            </div>
          </div>
        </div>

        {/* What residents could see */}
        <div className="mt-8">
          <SectionHeader kicker="THE VISION" title="What residents could see, without hunting" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {WHAT_RESIDENTS_SEE.map(({ icon: Icon, title, body }) => (
              <Card key={title} className="card-lift" data-testid="media-vision-card">
                <CardContent className="p-5">
                  <div className="h-10 w-10 rounded-xl bg-[#1a2744] flex items-center justify-center mb-3">
                    <Icon className="h-5 w-5 text-[#C9A84C]" />
                  </div>
                  <p className="font-serif font-bold text-[#1a2744] leading-snug">{title}</p>
                  <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Cube House Media: Steve's approved example (his position, not a City proposal) */}
        <div className="mt-8" data-testid="media-cubehouse-section">
          <SectionHeader kicker="AN EXAMPLE" title="Cube House Media" />
          <div className="bg-[#1a2744] rounded-2xl p-5 sm:p-7" data-testid="media-cubehouse-position">
            <FactBadge kind="steves_position" />
            <p className="mt-3 font-serif font-black text-white text-lg sm:text-xl leading-snug">Cube House Media is one example of the vision: mobile digital media that can bring information directly into the community where people live, work and travel.</p>
            <p className="mt-3 text-slate-300 text-sm sm:text-base leading-relaxed">Residents should not have to hunt through government websites, long reports and meeting agendas just to understand what their local government is doing. Steve believes we should use modern communication tools to make important public information easier to see and understand.</p>
            <p className="mt-3 text-slate-300 text-sm sm:text-base leading-relaxed">This is not about replacing official City communication. It is about making public information more visible, understandable and accessible.</p>
            <p className="mt-4 border-l-4 border-[#C9A84C] pl-3.5 font-serif font-bold text-[#e8c96a] text-base sm:text-lg" data-testid="media-cubehouse-line">Don't make people hunt for the information. Bring the information to the people.</p>
            <p className="mt-4 text-xs text-slate-400" data-testid="media-cubehouse-disclaimer">This is Steve's position and example, not an official City of Kawartha Lakes proposal.</p>
          </div>
        </div>

        {/* Already built */}
        <div className="mt-8">
          <SectionHeader kicker="ALREADY WORKING" title="This website is the proof of concept" dek="Steve did not wait to be elected to start. These tools already bring information to residents today." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ALREADY_BUILT.map((t) => (
              <Card key={t.title} className="card-lift" data-testid="media-tool-card">
                <CardContent className="p-5 flex flex-col h-full">
                  <p className="font-serif font-bold text-[#1a2744]">{t.title}</p>
                  <p className="text-sm text-slate-600 mt-1.5 flex-1">{t.body}</p>
                  <Button asChild className="mt-3 h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press w-full" data-testid={`media-tool-button-${t.url.replace(/\//g, '')}`}>
                    <Link to={t.url}>{t.label} <ArrowRight className="h-4 w-4 ml-1" /></Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <PositionBox title="Information is a service, just like roads and snow removal." testId="media-position-band">
            <p>Residents pay for their government. They should never have to dig for what it is doing. Steve's commitment is to keep building and championing channels that deliver public information to the people, clearly, honestly, and on time.</p>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <Button asChild className="h-11 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="media-alerts-button"><Link to="/alerts">Subscribe to Public Alerts <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
              <Button asChild variant="outline" className="h-11 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press" data-testid="media-newsroom-button"><Link to="/newsroom"><Newspaper className="h-4 w-4 mr-1.5" /> Visit the Newsroom</Link></Button>
            </div>
          </PositionBox>
        </div>
      </section>
    </div>
  );
}
